/**
 * InfinitSales - Notifications Context
 * Estado global do feed de notificacoes internas (sino + painel + toast).
 *
 * - Hidrata via GET /api/notifications quando autentica.
 * - Atualiza em tempo real via SSE (NOTIFICATION_CREATED / NOTIFICATION_READ).
 * - Dedup por id (seenIdsRef) para nao contar 2x quando a propria criacao local
 *   e o evento SSE chegam para o mesmo doc.
 *
 * Deve ficar DENTRO de SSEProvider e AuthProvider (ver App.js).
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useAuth } from './AuthContext';
import { useSSEEvent, SSE_EVENTS } from './SSEContext';
import notificationsService from '../services/global/notificationsService';
import { maybeShowNativeNotification } from '../utils/nativeNotifications';

const NotificationsContext = createContext(null);

export const NotificationsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toastQueue, setToastQueue] = useState([]);

  // Ids ja conhecidos — evita duplicar/contar 2x entre criacao local e SSE.
  const seenIdsRef = useRef(new Set());

  // Filtro por categoria (chips) + paginacao ("carregar mais"). O feed acumula
  // rapido; carregamos em paginas e filtramos por categoria no servidor.
  const PAGE_SIZE = 20;
  const [activeCategory, setActiveCategory] = useState(null);
  const activeCategoryRef = useRef(null);  // lido dentro do ingest (SSE)
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const nextOffsetRef = useRef(0);
  // Token de geração: descarta respostas obsoletas de load/loadMore quando a
  // categoria muda no meio do voo (evita misturar itens/offset de outra query).
  const reqSeqRef = useRef(0);
  // Categorias presentes no feed — monta os chips sem opcoes vazias.
  const [availableCategories, setAvailableCategories] = useState([]);

  /** Injeta um doc novo no estado de forma idempotente. */
  const ingest = useCallback((doc, { toast = false } = {}) => {
    if (!doc || !doc.id) return;
    if (seenIdsRef.current.has(doc.id)) return; // ja conhecido
    seenIdsRef.current.add(doc.id);
    // Respeita o filtro de categoria ativo na LISTA; o toast e a bolinha valem
    // para qualquer categoria (a bolinha e o total real de nao-lidas).
    const cat = activeCategoryRef.current;
    if (!cat || doc.category === cat) {
      setNotifications((prev) => [doc, ...prev]);
      nextOffsetRef.current += 1;  // mantem o offset alinhado com o append
    }
    if (!doc.read) setUnreadCount((c) => c + 1);
    if (toast && doc.show_toast) setToastQueue((q) => [...q, doc]);
  }, []);

  // Hidrata a 1ª página (opcionalmente filtrada por categoria). Reseta o dedup.
  const load = useCallback(async (category) => {
    if (!isAuthenticated) return;
    const seq = ++reqSeqRef.current;
    setLoading(true);
    try {
      const data = await notificationsService.list({
        limit: PAGE_SIZE, category: category || null, offset: 0,
      });
      if (seq !== reqSeqRef.current) return;  // resposta obsoleta (trocou o filtro)
      const items = data?.items || [];
      seenIdsRef.current = new Set(items.map((n) => n.id));
      setNotifications(items);
      setUnreadCount(data?.unread || 0);
      setHasMore(!!data?.has_more);
      nextOffsetRef.current = data?.next_offset || items.length;
      // Só recomputa os chips a partir do feed COMPLETO (sem filtro), senão a
      // lista de categorias colapsaria para a única filtrada.
      if (!category) {
        setAvailableCategories(
          [...new Set(items.map((n) => n.category).filter(Boolean))]
        );
      }
    } catch (e) {
      // silencioso — bolinha/painel apenas nao hidratam
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const refresh = useCallback(() => load(activeCategoryRef.current), [load]);

  // Troca o chip de categoria e recarrega a 1ª página desse filtro.
  const setCategory = useCallback((cat) => {
    const next = cat || null;
    activeCategoryRef.current = next;
    setActiveCategory(next);
    load(next);
  }, [load]);

  // "Carregar mais": próxima página (append, deduplicado).
  const loadMore = useCallback(async () => {
    if (!isAuthenticated || !hasMore || loadingMore) return;
    const seq = ++reqSeqRef.current;
    setLoadingMore(true);
    try {
      const data = await notificationsService.list({
        limit: PAGE_SIZE,
        category: activeCategoryRef.current || null,
        offset: nextOffsetRef.current,
      });
      if (seq !== reqSeqRef.current) return;  // categoria trocou no meio do voo
      const items = data?.items || [];
      const fresh = items.filter((n) => !seenIdsRef.current.has(n.id));
      fresh.forEach((n) => seenIdsRef.current.add(n.id));
      setNotifications((prev) => [...prev, ...fresh]);
      setHasMore(!!data?.has_more);
      nextOffsetRef.current = data?.next_offset
        || (nextOffsetRef.current + items.length);
    } catch (e) {
      // silencioso
    } finally {
      setLoadingMore(false);
    }
  }, [isAuthenticated, hasMore, loadingMore]);

  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      seenIdsRef.current = new Set();
      setNotifications([]);
      setUnreadCount(0);
      setToastQueue([]);
      activeCategoryRef.current = null;
      setActiveCategory(null);
      setHasMore(false);
      setAvailableCategories([]);
      nextOffsetRef.current = 0;
    }
  }, [isAuthenticated, refresh]);

  // SSE: nova notificacao -> entra no feed, incrementa bolinha e (se show_toast)
  // dispara o card no canto. Com o app em SEGUNDO PLANO, mostra tambem a
  // notificacao NATIVA do dispositivo (Android/desktop; exige permissao — ver
  // utils/nativeNotifications).
  useSSEEvent(
    SSE_EVENTS.NOTIFICATION_CREATED,
    (data) => {
      ingest(data, { toast: true });
      if (data?.show_toast) maybeShowNativeNotification(data);
    },
    []
  );

  // Volta do background (mobile congela a aba e o SSE perde eventos): re-hidrata
  // a 1a pagina + bolinha. Throttle de 15s para alternancia rapida de abas.
  const lastVisRefreshRef = useRef(0);
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisRefreshRef.current < 15_000) return;
      lastVisRefreshRef.current = now;
      refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isAuthenticated, refresh]);

  // SSE: leitura sincronizada entre abas/dispositivos.
  useSSEEvent(
    SSE_EVENTS.NOTIFICATION_READ,
    (data) => {
      if (!data) return;
      if (data.all) {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadCount(0);
        return;
      }
      if (data.id) {
        // Só decrementa se o item AINDA estava não-lido — senão o markRead
        // otimista já decrementou e o eco do próprio tab contaria em dobro.
        setNotifications((prev) => {
          const t = prev.find((n) => n.id === data.id);
          if (t && !t.read) setUnreadCount((c) => Math.max(0, c - 1));
          return prev.map((n) => (n.id === data.id ? { ...n, read: true } : n));
        });
      }
    },
    []
  );

  const markRead = useCallback(async (id) => {
    setNotifications((prev) => {
      const target = prev.find((n) => n.id === id);
      if (target && !target.read) setUnreadCount((c) => Math.max(0, c - 1));
      return prev.map((n) => (n.id === id ? { ...n, read: true } : n));
    });
    try {
      await notificationsService.markRead(id);
    } catch (e) {
      /* mantem otimista */
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await notificationsService.markAllRead();
    } catch (e) {
      /* mantem otimista */
    }
  }, []);

  const remove = useCallback(async (id) => {
    const wasLoaded = seenIdsRef.current.has(id);
    setNotifications((prev) => {
      const target = prev.find((n) => n.id === id);
      if (target && !target.read) setUnreadCount((c) => Math.max(0, c - 1));
      return prev.filter((n) => n.id !== id);
    });
    seenIdsRef.current.delete(id);
    // Espelha o ingest (+1 no prepend): a lista do servidor encolheu, então o
    // offset da próxima página precisa recuar 1 pra não pular um item não visto.
    if (wasLoaded) nextOffsetRef.current = Math.max(0, nextOffsetRef.current - 1);
    try {
      await notificationsService.remove(id);
    } catch (e) {
      /* mantem otimista */
    }
  }, []);

  /** Cria notificacao individual (ex.: pelo menu do chat). Sem auto-toast. */
  const createNotification = useCallback(
    async (payload) => {
      const doc = await notificationsService.create(payload);
      ingest(doc, { toast: false });
      return doc;
    },
    [ingest]
  );

  const currentToast = toastQueue[0] || null;
  const dismissToast = useCallback(() => {
    setToastQueue((q) => q.slice(1));
  }, []);

  /**
   * Empurra um toast SINTÉTICO no canto (sem entrar no feed nem contar como
   * não-lido). Usado quando o clique numa notificação NÃO abre um chat — ex.:
   * o contato foi retransferido / saiu do workspace, ou o CTA não tem destino.
   */
  const pushToast = useCallback(({ title, body, accentColor } = {}) => {
    setToastQueue((q) => [
      ...q,
      {
        id: `local-${q.length}-${(title || '').slice(0, 12)}`,
        title: title || 'Aviso',
        body: body || '',
        show_toast: true,
        accent_color: accentColor || null,
        read: true,
      },
    ]);
  }, []);

  const value = {
    notifications,
    unreadCount,
    loading,
    refresh,
    markRead,
    markAllRead,
    remove,
    createNotification,
    currentToast,
    dismissToast,
    pushToast,
    // Filtro por categoria + paginacao.
    activeCategory,
    availableCategories,
    setCategory,
    hasMore,
    loadingMore,
    loadMore,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return ctx;
};

export default NotificationsContext;
